// src/pages/modals/CajaModal.js
//
// v2.46 · Caja POS: cierre en 3 etapas.
//         · PIN de revisión y PIN de cierre pueden ser la misma credencial; se validan dos veces.
// ------------------------------------------------------------------------------

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Divider,
  CircularProgress,
  TextField,
  Button,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Stack,
  Paper,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as BalanceIcon,
  TrendingUp as SalesIcon,
  CreditCard as CardIcon,
  PointOfSale as SaleIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  RestartAlt as ResetIcon,
  InfoOutlined as InfoIcon,
  Inventory2Outlined as InventoryIcon,
  History as HistoryIcon,
  QrCodeScanner as ScannerIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';

import api from '../../api/api';
import ConteoTabacoModal from './ConteoTabacoModal';
import PinConfirmModal from './PinConfirmModal';

const CLP = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const fmtCLP = (n) => CLP.format(Math.round(Number(n) || 0));

const DENOMINATIONS = [
  { value: 20000, label: '20.000', img: '/assets/b20000.jpg' },
  { value: 10000, label: '10.000', img: '/assets/b10000.jpg' },
  { value: 5000, label: '5.000', img: '/assets/b5000.jpg' },
  { value: 2000, label: '2.000', img: '/assets/b2000.jpg' },
  { value: 1000, label: '1.000', img: '/assets/b1000.jpg' },
  { value: 500, label: '500', img: '/assets/m500.jpg' },
  { value: 100, label: '100', img: '/assets/m100.jpg' },
  { value: 50, label: '50', img: '/assets/m50.jpg' },
  { value: 10, label: '10', img: '/assets/m10.jpg' },
];

const BILLS = DENOMINATIONS.filter((d) => d.value >= 1000);
const COINS = DENOMINATIONS.filter((d) => d.value < 1000);

function formatCLPInput(v) {
  const nums = String(v || '').replace(/\D/g, '');
  if (nums === '') return '';

  const parsed = parseInt(nums, 10);
  return new Intl.NumberFormat('es-CL').format(Number.isNaN(parsed) ? 0 : parsed);
}

function parseCLPInput(v) {
  return parseInt(String(v || '').replace(/\./g, ''), 10) || 0;
}

const clampInt = (v) => Math.max(0, parseInt(String(v || '0').replace(/\D/g, ''), 10) || 0);

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizarClaveMedioFrontend = (raw) =>
  String(raw || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

const requiereMontoCierreMedioFrontend = (medio) => {
  const tipo = normalizarClaveMedioFrontend(medio?.tipo || 'OTRO');

  if (medio?.usa_terminal === true) return true;

  return (
    medio?.requiere_liquidacion === true &&
    ['TARJETA', 'PASARELA'].includes(tipo)
  );
};

function normalizeCajaResponse(data) {
  if (!data) return null;

  if (data?.caja && typeof data.caja === 'object') return data.caja;

  if (data?.data?.caja && typeof data.data.caja === 'object') return data.data.caja;

  if (data?.data && typeof data.data === 'object') return data.data;

  return data;
}

function esMovimientoVentaLegacy(movimiento) {
  const tipo = String(movimiento?.tipo || '').trim().toLowerCase();
  const descripcion = String(movimiento?.descripcion || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (tipo !== 'ingreso') return false;

  return (
    descripcion.startsWith('ingreso efectivo por venta pos') ||
    descripcion.includes('efectivo por venta pos #')
  );
}

function resumirMovimientosManuales(rows) {
  const movimientos = Array.isArray(rows) ? rows : [];

  return movimientos.reduce(
    (acc, movimiento) => {
      const tipo = String(movimiento?.tipo || '').trim().toLowerCase();
      const monto = Math.abs(toNumber(movimiento?.monto));

      if (!monto || esMovimientoVentaLegacy(movimiento)) return acc;

      if (tipo === 'ingreso') acc.ingresos += monto;
      if (tipo === 'egreso') acc.egresos += monto;

      return acc;
    },
    { ingresos: 0, egresos: 0 }
  );
}

function normalizarDetalleTabacoFrontend(tab) {
  if (typeof tab !== 'object' || tab === null || Array.isArray(tab)) return {};

  const out = {};

  for (const [k, v] of Object.entries(tab)) {
    if (!/^\d+$/.test(String(k))) continue;

    const n = parseInt(v, 10);
    out[String(k)] = Number.isNaN(n) || n < 0 ? 0 : n;
  }

  return out;
}

function filtrarDetalleTabacoPositivoFrontend(tab) {
  const normalizado = normalizarDetalleTabacoFrontend(tab);

  return Object.fromEntries(
    Object.entries(normalizado).filter(([, qty]) => (parseInt(qty, 10) || 0) > 0)
  );
}

function hasDetalleTabaco(tab) {
  return Object.keys(filtrarDetalleTabacoPositivoFrontend(tab)).length > 0;
}

function sumarDetalleTabacoFrontend(tab) {
  const normalizado = normalizarDetalleTabacoFrontend(tab);

  return Object.values(normalizado).reduce((sum, v) => {
    const n = parseInt(v, 10);
    return sum + (Number.isNaN(n) ? 0 : n);
  }, 0);
}

function sumarReposicionesTabacoFrontend(caja) {
  const out = {};
  const reposiciones = Array.isArray(caja?.detalle_tabaco_reposiciones)
    ? caja.detalle_tabaco_reposiciones
    : [];

  reposiciones.forEach((r) => {
    const detalle = normalizarDetalleTabacoFrontend(r?.reposicion || {});
    Object.entries(detalle).forEach(([id, qty]) => {
      out[String(id)] = (out[String(id)] || 0) + (parseInt(qty, 10) || 0);
    });
  });

  return out;
}

function idsTabacoCajaFrontend(caja) {
  const ids = new Set();

  // Una clave con cantidad 0 no convierte por sí sola al producto en parte
  // del conteo del turno. Esto evita arrastrar productos agotados desde un
  // cierre anterior y exigir luego contar físicamente algo que no existe.
  const addPositive = (obj) => {
    const detalle = normalizarDetalleTabacoFrontend(obj || {});

    Object.entries(detalle).forEach(([id, qty]) => {
      if ((parseInt(qty, 10) || 0) > 0) ids.add(String(id));
    });
  };

  addPositive(caja?.detalle_tabaco_entrega);
  addPositive(caja?.detalle_tabaco_vendido);
  addPositive(caja?.detalle_tabaco_cierre);
  addPositive(sumarReposicionesTabacoFrontend(caja));

  return Array.from(ids).sort((a, b) => Number(a) - Number(b));
}

const CIERRE_CAJA_DRAFT_PREFIX = 'IntegraNubePOS:cierre-caja';
const CIERRE_TABACO_DRAFT_PREFIX = 'IntegraNubePOS:conteo-tabaco-cierre';

function getTenantScopeFrontend() {
  try {
    const flat = String(localStorage.getItem('tenantid') || '').trim();
    if (flat) return flat;

    const tenant = JSON.parse(localStorage.getItem('tenant') || 'null');
    const fromTenant = String(tenant?.tenantid || tenant?.tenantId || tenant?.id || '').trim();
    if (fromTenant) return fromTenant;

    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const fromUser = String(user?.tenantid || user?.tenantId || '').trim();
    if (fromUser) return fromUser;
  } catch (_) {}

  return 'default';
}

function getCierreCajaDraftKey(cajaId) {
  return `${CIERRE_CAJA_DRAFT_PREFIX}:${getTenantScopeFrontend()}:${String(cajaId || 'sin-caja')}`;
}

function getCierreTabacoDraftKey(cajaId) {
  return `${CIERRE_TABACO_DRAFT_PREFIX}:${getTenantScopeFrontend()}:${String(cajaId || 'sin-caja')}`;
}

function readLocalJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeLocalJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (_) {
    return false;
  }
}

function clearCierreCajaDrafts(cajaId) {
  if (!cajaId) return;
  try {
    localStorage.removeItem(getCierreCajaDraftKey(cajaId));
    localStorage.removeItem(getCierreTabacoDraftKey(cajaId));
  } catch (_) {}
}

function getProductBarcodeFrontend(product) {
  return String(
    product?.codigo_barras ||
      product?.codigo_barra ||
      product?.codigoBarras ||
      product?.codigoBarra ||
      product?.barcode ||
      product?.bar_code ||
      product?.ean ||
      product?.upc ||
      ''
  ).trim();
}

function StatCard({ icon, title, value, sub }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
      }}
    >
      <Box sx={{ mt: 0.25 }}>{icon}</Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>

        <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
          {value}
        </Typography>

        {sub ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}

StatCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  sub: PropTypes.string,
};

StatCard.defaultProps = {
  sub: '',
};

function ConteoTabacoCierreModal({ open, onClose, cajaData, onConfirm, loading = false }) {
  const caja = useMemo(() => normalizeCajaResponse(cajaData) || {}, [cajaData]);
  const [productosMap, setProductosMap] = useState({});
  const [conteo, setConteo] = useState({});
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [error, setError] = useState('');
  const [scanEnabled, setScanEnabled] = useState(true);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanProductId, setScanProductId] = useState(null);
  const [scanQuantity, setScanQuantity] = useState('');
  const [scanFeedback, setScanFeedback] = useState(null);
  const [draftRecoveredAt, setDraftRecoveredAt] = useState(null);
  const [draftReady, setDraftReady] = useState(false);

  const scanBufferRef = useRef('');
  const scanLastKeyAtRef = useRef(0);
  const scanIdleTimerRef = useRef(null);
  const scanCaptureRef = useRef(null);
  const restoredSignatureRef = useRef(null);
  const quantityInputRef = useRef(null);

  const productIds = useMemo(() => idsTabacoCajaFrontend(caja), [caja]);
  const draftKey = useMemo(
    () => (caja?.id ? getCierreTabacoDraftKey(caja.id) : null),
    [caja?.id]
  );

  useEffect(() => {
    if (!open) {
      setConteo({});
      setProductosMap({});
      setError('');
      setScanDialogOpen(false);
      setScanProductId(null);
      setScanQuantity('');
      setScanFeedback(null);
      setDraftRecoveredAt(null);
      setDraftReady(false);
      scanBufferRef.current = '';
      scanLastKeyAtRef.current = 0;
      restoredSignatureRef.current = null;
      return undefined;
    }

    const signature = `${draftKey || 'sin-draft'}:${productIds.join(',')}`;
    if (restoredSignatureRef.current === signature) return undefined;

    const initial = Object.fromEntries(productIds.map((id) => [String(id), '']));
    const draft = draftKey ? readLocalJson(draftKey) : null;

    if (draft?.detalle && typeof draft.detalle === 'object') {
      productIds.forEach((id) => {
        const raw = draft.detalle?.[String(id)];
        if (raw !== undefined && raw !== null && raw !== '') {
          initial[String(id)] = String(Math.max(0, parseInt(raw, 10) || 0));
        }
      });
      setDraftRecoveredAt(draft.updatedAt || null);
    } else {
      setDraftRecoveredAt(null);
    }

    setConteo(initial);
    restoredSignatureRef.current = signature;
    setDraftReady(true);
    return undefined;
  }, [open, productIds, draftKey]);

  useEffect(() => {
    if (!open || !draftReady || !draftKey || !caja?.id) return undefined;

    writeLocalJson(draftKey, {
      version: 1,
      cajaId: caja.id,
      tenantid: getTenantScopeFrontend(),
      updatedAt: new Date().toISOString(),
      detalle: conteo,
    });

    return undefined;
  }, [open, draftReady, draftKey, caja?.id, conteo]);

  useEffect(() => {
    if (!open || !productIds.length) {
      setProductosMap({});
      return undefined;
    }

    let mounted = true;
    setLoadingProductos(true);
    setError('');

    Promise.all(
      productIds.map(async (pid) => {
        try {
          const { data } = await api.get(`/inventario_productos/${pid}/detalles`);
          const producto = data?.producto || data?.prod || data?.data || data || {};
          return [
            String(pid),
            producto?.id
              ? producto
              : { id: Number(pid), nombre: `Producto #${pid}`, sku: '' },
          ];
        } catch {
          return [String(pid), { id: Number(pid), nombre: `Producto #${pid}`, sku: '' }];
        }
      })
    )
      .then((rows) => {
        if (mounted) setProductosMap(Object.fromEntries(rows));
      })
      .catch(() => {
        if (mounted) setError('No se pudieron cargar todos los productos. El conteo puede continuar.');
      })
      .finally(() => {
        if (mounted) setLoadingProductos(false);
      });

    return () => {
      mounted = false;
    };
  }, [open, productIds]);

  const completos = productIds.every((id) => {
    const raw = conteo?.[String(id)];
    return raw !== '' && raw !== null && raw !== undefined;
  });

  const completados = productIds.filter((id) => {
    const raw = conteo?.[String(id)];
    return raw !== '' && raw !== null && raw !== undefined;
  }).length;

  const findProductIdByScan = useCallback((rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) return null;

    const directId = productIds.find((id) => String(id) === code);
    if (directId) return String(directId);

    const match = productIds.find((id) => {
      const producto = productosMap?.[String(id)] || {};
      const barcode = getProductBarcodeFrontend(producto);
      const sku = String(producto?.sku || producto?.codigo || '').trim();
      return barcode === code || sku === code;
    });

    return match ? String(match) : null;
  }, [productIds, productosMap]);

  const openScannedProduct = useCallback((rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) return;

    const pid = findProductIdByScan(code);

    if (!pid) {
      setScanFeedback({
        severity: 'warning',
        message: `Código ${code} no corresponde a un producto de este conteo.`,
      });
      return;
    }

    setScanFeedback(null);
    setScanProductId(pid);
    setScanQuantity(conteo?.[pid] === '' || conteo?.[pid] == null ? '' : String(conteo[pid]));
    setScanDialogOpen(true);

    setTimeout(() => {
      quantityInputRef.current?.focus?.();
      quantityInputRef.current?.select?.();
    }, 80);
  }, [findProductIdByScan, conteo]);

  useEffect(() => {
    if (!open || !scanEnabled || scanDialogOpen || loadingProductos) return undefined;

    const focusScanner = () => {
      requestAnimationFrame(() => {
        scanCaptureRef.current?.focus?.({ preventScroll: true });
      });
    };

    const clearIdleTimer = () => {
      if (scanIdleTimerRef.current) {
        window.clearTimeout(scanIdleTimerRef.current);
        scanIdleTimerRef.current = null;
      }
    };

    const armIdleTimer = () => {
      clearIdleTimer();
      scanIdleTimerRef.current = window.setTimeout(() => {
        scanBufferRef.current = '';
        scanLastKeyAtRef.current = 0;
        scanIdleTimerRef.current = null;
      }, 5000);
    };

    const consumeEvent = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };

    const handler = (event) => {
      if (event.ctrlKey || event.altKey || event.metaKey) return;

      const target = event.target;
      const tag = String(target?.tagName || '').toLowerCase();
      const editing = tag === 'input' || tag === 'textarea' || target?.isContentEditable;
      if (editing) return;

      if (event.key === 'Enter') {
        consumeEvent(event);
        clearIdleTimer();

        const code = scanBufferRef.current.trim();
        scanBufferRef.current = '';
        scanLastKeyAtRef.current = 0;

        if (code.length >= 3) {
          openScannedProduct(code);
        } else if (code.length > 0) {
          setScanFeedback({
            severity: 'warning',
            message: 'Código demasiado corto. Escanee o escriba el código completo.',
          });
          focusScanner();
        }
        return;
      }

      if (event.key === 'Escape') {
        if (scanBufferRef.current) {
          consumeEvent(event);
          clearIdleTimer();
          scanBufferRef.current = '';
          scanLastKeyAtRef.current = 0;
          focusScanner();
        }
        return;
      }

      if (event.key === 'Backspace' && scanBufferRef.current) {
        consumeEvent(event);
        scanBufferRef.current = scanBufferRef.current.slice(0, -1);
        scanLastKeyAtRef.current = Date.now();
        armIdleTimer();
        return;
      }

      if (event.key.length === 1) {
        consumeEvent(event);
        scanBufferRef.current += event.key;
        scanLastKeyAtRef.current = Date.now();
        armIdleTimer();
      }
    };

    focusScanner();
    window.addEventListener('keydown', handler, true);

    return () => {
      window.removeEventListener('keydown', handler, true);
      clearIdleTimer();
    };
  }, [open, scanEnabled, scanDialogOpen, loadingProductos, openScannedProduct]);

  const saveScannedQuantity = () => {
    if (!scanProductId) return;

    const raw = String(scanQuantity ?? '').trim();
    if (raw === '') {
      setScanFeedback({ severity: 'warning', message: 'Ingrese la cantidad física.' });
      quantityInputRef.current?.focus?.();
      return;
    }

    const qty = Math.max(0, parseInt(raw.replace(/\D/g, ''), 10) || 0);
    const producto = productosMap?.[String(scanProductId)] || {};

    setConteo((prev) => ({ ...prev, [String(scanProductId)]: String(qty) }));
    setScanDialogOpen(false);
    setScanProductId(null);
    setScanQuantity('');
    setScanFeedback({
      severity: 'success',
      message: `${producto?.nombre || `Producto #${scanProductId}`}: ${qty} unidad(es).`,
    });
    scanBufferRef.current = '';
    scanLastKeyAtRef.current = 0;
    if (scanIdleTimerRef.current) {
      window.clearTimeout(scanIdleTimerRef.current);
      scanIdleTimerRef.current = null;
    }
    setTimeout(() => {
      scanCaptureRef.current?.focus?.({ preventScroll: true });
    }, 40);
  };

  const marcarProductoEnCero = (productoId, producto = null) => {
    const pid = String(productoId || '');
    if (!pid) return;

    setConteo((prev) => ({
      ...(prev || {}),
      [pid]: '0',
    }));

    setScanFeedback({
      severity: 'info',
      message: `${producto?.nombre || `Producto #${pid}`}: confirmado sin unidades físicas.`,
    });

    scanBufferRef.current = '';
    scanLastKeyAtRef.current = 0;

    setTimeout(() => {
      scanCaptureRef.current?.focus?.({ preventScroll: true });
    }, 40);
  };

  const resetConteo = () => {
    if (!window.confirm('¿Reiniciar el conteo de cigarrillos?')) return;
    setConteo(Object.fromEntries(productIds.map((id) => [String(id), ''])));
    setDraftRecoveredAt(null);
    setScanFeedback({ severity: 'info', message: 'Conteo reiniciado.' });
  };

  const handleConfirm = () => {
    if (!completos) return;

    const detalle = Object.fromEntries(
      productIds.map((id) => [String(id), Math.max(0, parseInt(conteo?.[String(id)], 10) || 0)])
    );

    const productos = productIds.map((id) => ({
      id: String(id),
      nombre: productosMap?.[String(id)]?.nombre || `Producto #${id}`,
      sku: productosMap?.[String(id)]?.sku || '',
      codigo_barras: getProductBarcodeFrontend(productosMap?.[String(id)] || {}),
      precio_neto: productosMap?.[String(id)]?.precio_neto ?? null,
      precio: productosMap?.[String(id)]?.precio ?? null,
      precio_bruto: productosMap?.[String(id)]?.precio_bruto ?? null,
    }));

    const totalUnidades = sumarDetalleTabacoFrontend(detalle);
    const totalMonetario = productIds.reduce((sum, id) => {
      const producto = productosMap?.[String(id)] || {};
      const qty = Number(detalle?.[String(id)] || 0);
      const precio = Number(
        producto?.precio_neto ??
          producto?.precio ??
          producto?.precio_bruto ??
          0
      );

      return sum + (Number.isFinite(precio) ? precio * qty : 0);
    }, 0);

    onConfirm?.(detalle, {
      total_productos_recibidos: totalUnidades,
      total_productos: productIds.length,
      total_monetario: totalMonetario,
      productos,
    });
  };

  const scanProduct = scanProductId ? productosMap?.[String(scanProductId)] || null : null;

  return (
    <>
      <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={900}>
              Conteo de cigarrillos
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Escanee el producto para registrar o modificar la cantidad.
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={loading} aria-label="Cerrar conteo de cigarrillos">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent
          ref={scanCaptureRef}
          tabIndex={-1}
          sx={{ outline: 'none' }}
        >
          <Stack spacing={2}>
            {error ? <Alert severity="warning">{error}</Alert> : null}

            {scanFeedback ? (
              <Alert severity={scanFeedback.severity || 'info'} onClose={() => setScanFeedback(null)}>
                {scanFeedback.message}
              </Alert>
            ) : null}

            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<ScannerIcon />}
                    color={scanEnabled ? 'success' : 'default'}
                    label={scanEnabled ? 'Escáner activo' : 'Escáner pausado'}
                    variant={scanEnabled ? 'filled' : 'outlined'}
                  />
                  <Chip
                    size="small"
                    label={`${completados} de ${productIds.length}`}
                    color={completos ? 'success' : 'default'}
                  />
                  {draftRecoveredAt ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Conteo recuperado"
                    />
                  ) : null}
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ScannerIcon />}
                    onClick={() => {
                      setScanEnabled((prev) => {
                        const next = !prev;
                        if (next) {
                          setTimeout(() => {
                            scanCaptureRef.current?.focus?.({ preventScroll: true });
                          }, 40);
                        }
                        return next;
                      });
                      scanBufferRef.current = '';
                      scanLastKeyAtRef.current = 0;
                      if (scanIdleTimerRef.current) {
                        window.clearTimeout(scanIdleTimerRef.current);
                        scanIdleTimerRef.current = null;
                      }
                    }}
                    disabled={loading}
                  >
                    {scanEnabled ? 'Pausar escáner' : 'Activar escáner'}
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<ResetIcon />}
                    onClick={resetConteo}
                    disabled={loading}
                  >
                    Reiniciar
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {loadingProductos ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : !productIds.length ? (
              <Alert severity="info">No hay productos de cigarrillos asociados a esta caja.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell>SKU / Código</TableCell>
                    <TableCell align="center" sx={{ width: 105 }}>Estado</TableCell>
                    <TableCell align="right" sx={{ width: 170 }}>Cantidad física</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productIds.map((pid) => {
                    const producto = productosMap?.[String(pid)] || {
                      id: Number(pid),
                      nombre: `Producto #${pid}`,
                      sku: '',
                    };
                    const contado = conteo?.[String(pid)] !== '' && conteo?.[String(pid)] != null;
                    const barcode = getProductBarcodeFrontend(producto);

                    return (
                      <TableRow key={pid} sx={contado ? { bgcolor: 'action.hover' } : undefined}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {producto?.nombre || `Producto #${pid}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{producto?.sku || '—'}</Typography>
                          {barcode ? (
                            <Typography variant="caption" color="text.secondary">
                              {barcode}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="center">
                          {contado ? (
                            <Chip size="small" icon={<CheckIcon />} color="success" label="Contado" variant="outlined" />
                          ) : (
                            <Chip size="small" label="Pendiente" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {contado ? (
                            <Typography
                              variant="body1"
                              fontWeight={900}
                              color="text.primary"
                            >
                              {Math.max(0, parseInt(conteo?.[String(pid)], 10) || 0)}
                            </Typography>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              onClick={() => marcarProductoEnCero(pid, producto)}
                              disabled={loading}
                            >
                              Marcar 0
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Volver</Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading || loadingProductos || !completos || !productIds.length}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={scanDialogOpen}
        onClose={() => {
          if (loading) return;
          setScanDialogOpen(false);
          setScanProductId(null);
          setScanQuantity('');
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Registrar cantidad</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Box>
              <Typography fontWeight={900}>
                {scanProduct?.nombre || (scanProductId ? `Producto #${scanProductId}` : 'Producto')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[scanProduct?.sku, getProductBarcodeFrontend(scanProduct || {})].filter(Boolean).join(' · ') || 'Sin código disponible'}
              </Typography>
            </Box>
            <TextField
              inputRef={quantityInputRef}
              label="Cantidad física"
              value={scanQuantity}
              onChange={(e) => setScanQuantity(String(e.target.value || '').replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  saveScannedQuantity();
                }
              }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 0 }}
              autoFocus
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setScanDialogOpen(false);
              setScanProductId(null);
              setScanQuantity('');
            }}
          >
            Cancelar
          </Button>
          <Button variant="contained" onClick={saveScannedQuantity}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

ConteoTabacoCierreModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cajaData: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

ConteoTabacoCierreModal.defaultProps = {
  cajaData: null,
  loading: false,
};

export default function CajaModal({ open, onClose: onCloseModal }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isAbierta, setIsAbierta] = useState(false);
  const [cajaData, setCajaData] = useState(null);

  const [montoInicialStr, setMontoInicialStr] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [pdfUrl, setPdfUrl] = useState(null);
  const [showArqueo, setShowArqueo] = useState(true);

  const [counts, setCounts] = useState({});
  const [pendingMonto, setPendingMonto] = useState(null);

  const [nextAction, setNextAction] = useState(null);
  const [tabacoModalOpen, setTabacoModalOpen] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinPurpose, setPinPurpose] = useState(null);

  const [usarCierreAnteriorTabaco, setUsarCierreAnteriorTabaco] = useState(false);
  const [ultimoCierreTabaco, setUltimoCierreTabaco] = useState(null);
  const [tabacoBaseApertura, setTabacoBaseApertura] = useState({});
  const [loadingUltimoCierreTabaco, setLoadingUltimoCierreTabaco] = useState(false);

  const [confirmacionMediosInput, setConfirmacionMediosInput] = useState({});

  const [resumenMovimientosManuales, setResumenMovimientosManuales] = useState({
    ingresos: 0,
    egresos: 0,
    cargado: false,
  });

  const [arqueoConfirmado, setArqueoConfirmado] = useState(false);
  const [supervisorWaitingOpen, setSupervisorWaitingOpen] = useState(false);
  const [supervisorReviewOpen, setSupervisorReviewOpen] = useState(false);
  const [pendingCloseTabacoDetalle, setPendingCloseTabacoDetalle] = useState(null);
  const [pendingCloseTabacoTotals, setPendingCloseTabacoTotals] = useState(null);
  const [editingArqueoRevision, setEditingArqueoRevision] = useState(false);
  const [editingTabacoRevision, setEditingTabacoRevision] = useState(false);
  const [arqueoRevisionCounts, setArqueoRevisionCounts] = useState({});
  const [arqueoRevisionMediosInput, setArqueoRevisionMediosInput] = useState({});
  const [tabacoRevisionDetalleInput, setTabacoRevisionDetalleInput] = useState({});

  const formRef = useRef(null);
  const pinAuthorizationTokenRef = useRef(null);
  const cierreDraftClosedRef = useRef(null);
  const [cierreDraftReadyKey, setCierreDraftReadyKey] = useState(null);

  const caja = useMemo(() => normalizeCajaResponse(cajaData) || {}, [cajaData]);

  const action = isAbierta ? 'cerrar' : 'abrir';

  const totalArqueo = useMemo(() => {
    return DENOMINATIONS.reduce((sum, { value }) => {
      const cnt = clampInt(counts[value]);
      return sum + cnt * value;
    }, 0);
  }, [counts]);

  const montoInicial = toNumber(caja?.monto_inicial);

  const movManual = toNumber(caja?.mov_manual);
  const ventasBruto = toNumber(caja?.ventas_total_bruto);
  const ventasEfectivo = toNumber(caja?.ventas_efectivo);

  const ingresosManuales = resumenMovimientosManuales.cargado
    ? toNumber(resumenMovimientosManuales.ingresos)
    : Math.max(movManual, 0);

  const retirosManuales = resumenMovimientosManuales.cargado
    ? toNumber(resumenMovimientosManuales.egresos)
    : Math.max(-movManual, 0);

  const movimientosManualesNetos = ingresosManuales - retirosManuales;

  const ventasTarjetaTotal = toNumber(caja?.ventas_tarjeta_total ?? caja?.ventas_debito);
  const ventasTarjetaDebito = toNumber(caja?.ventas_tarjeta_debito);
  const ventasTarjetaCredito = toNumber(caja?.ventas_tarjeta_credito);

  const ventasTransbank = toNumber(
    caja?.ventas_transbank ??
      caja?.saldo_teorico_transbank ??
      0
  );

  const mediosCierre = useMemo(() => {
    const medios = Array.isArray(caja?.medios_pago) ? caja.medios_pago : [];

    const dinamicos = medios
      .map((medio) => ({
        ...medio,
        clave: normalizarClaveMedioFrontend(medio?.clave),
        nombre: String(medio?.nombre || medio?.clave || 'Medio de pago'),
        total: toNumber(medio?.total),
      }))
      .filter(
        (medio) =>
          medio.clave &&
          medio.total > 0 &&
          requiereMontoCierreMedioFrontend(medio)
      );

    if (
      !dinamicos.some((medio) => medio.clave === 'TRANSBANK') &&
      ventasTransbank > 0
    ) {
      dinamicos.push({
        clave: 'TRANSBANK',
        nombre: 'Transbank',
        tipo: 'TARJETA',
        proveedor: 'TRANSBANK',
        total: ventasTransbank,
        requiere_liquidacion: true,
        usa_terminal: true,
      });
    }

    return dinamicos;
  }, [caja, ventasTransbank]);

  const confirmacionesMedios = useMemo(
    () =>
      mediosCierre.map((medio) => {
        const entrada = confirmacionMediosInput?.[medio.clave] || {};
        const rawMonto = String(entrada?.monto || '').trim();
        const monto = rawMonto === '' ? null : parseCLPInput(rawMonto);
        const referencia = String(entrada?.referencia || '').trim();

        return {
          ...medio,
          monto_reportado: monto,
          referencia,
          diferencia:
            monto === null
              ? null
              : monto - toNumber(medio.total),
        };
      }),
    [mediosCierre, confirmacionMediosInput]
  );

  const mediosCierreCapturados = confirmacionesMedios.every(
    (medio) => medio.monto_reportado !== null
  );

  const saldoEfectivo = toNumber(
    caja?.saldo_teorico_efectivo ??
      caja?.total_a_rendir_efectivo ??
      montoInicial + ventasEfectivo + movManual
  );

  const saldoTarjetaTotal = toNumber(caja?.saldo_teorico_tarjeta_total ?? caja?.saldo_teorico_debito);
  const saldoTarjetaDebito = toNumber(caja?.saldo_teorico_tarjeta_debito);
  const saldoTarjetaCredito = toNumber(caja?.saldo_teorico_tarjeta_credito);
  const saldoTransbank = toNumber(caja?.saldo_teorico_transbank ?? ventasTransbank);

  const detalleTabacoEntregaActual = useMemo(
    () => normalizarDetalleTabacoFrontend(caja?.detalle_tabaco_entrega || {}),
    [caja]
  );

  const requiereConteoTabacoCierre = useMemo(
    () => idsTabacoCajaFrontend(caja).length > 0,
    [caja]
  );

  const tabacoAperturaTotal = useMemo(
    () => sumarDetalleTabacoFrontend(detalleTabacoEntregaActual),
    [detalleTabacoEntregaActual]
  );

  const ventasTabacoTotal = Number(
    caja?.tabaco_vendido_total ??
      caja?.ventas_tabaco_total ??
      caja?.ventasTabacoTotal ??
      0
  );

  const diff = useMemo(() => totalArqueo - saldoEfectivo, [totalArqueo, saldoEfectivo]);

  const efectivoTeorico = saldoEfectivo;
  const efectivoAEntregarTesoreria = totalArqueo;

  const cuadre = useMemo(() => {
    if (diff === 0) return { label: 'CUADRE OK', severity: 'success', chipColor: 'success' };
    if (diff < 0) return { label: 'FALTANTE', severity: 'warning', chipColor: 'warning' };
    return { label: 'SOBRANTE', severity: 'error', chipColor: 'error' };
  }, [diff]);

  const canClose =
    action === 'cerrar' &&
    arqueoConfirmado &&
    mediosCierreCapturados;

  const countsRevisionActivos = editingArqueoRevision ? arqueoRevisionCounts : counts;
  const mediosRevisionActivos = editingArqueoRevision
    ? arqueoRevisionMediosInput
    : confirmacionMediosInput;

  const totalArqueoRevision = useMemo(
    () =>
      DENOMINATIONS.reduce((sum, { value }) => {
        const cnt = clampInt(countsRevisionActivos?.[value]);
        return sum + cnt * value;
      }, 0),
    [countsRevisionActivos]
  );

  const confirmacionesMediosRevision = useMemo(
    () =>
      mediosCierre.map((medio) => {
        const entrada = mediosRevisionActivos?.[medio.clave] || {};
        const rawMonto = String(entrada?.monto || '').trim();
        const monto = rawMonto === '' ? null : parseCLPInput(rawMonto);
        const referencia = String(entrada?.referencia || '').trim();

        return {
          ...medio,
          monto_reportado: monto,
          referencia,
          diferencia: monto === null ? null : monto - toNumber(medio.total),
        };
      }),
    [mediosCierre, mediosRevisionActivos]
  );

  const diffRevision = useMemo(
    () => totalArqueoRevision - saldoEfectivo,
    [totalArqueoRevision, saldoEfectivo]
  );

  const revisionTabacoRows = useMemo(() => {
    if (!pendingCloseTabacoDetalle) return [];

    const apertura = normalizarDetalleTabacoFrontend(caja?.detalle_tabaco_entrega || {});
    const vendidos = normalizarDetalleTabacoFrontend(caja?.detalle_tabaco_vendido || {});
    const reposiciones = sumarReposicionesTabacoFrontend(caja);
    const contado = normalizarDetalleTabacoFrontend(
      editingTabacoRevision ? tabacoRevisionDetalleInput : pendingCloseTabacoDetalle || {}
    );
    const meta = Object.fromEntries(
      (pendingCloseTabacoTotals?.productos || []).map((p) => [String(p.id), p])
    );

    const ids = new Set([
      ...Object.keys(apertura),
      ...Object.keys(vendidos),
      ...Object.keys(reposiciones),
      ...Object.keys(contado),
    ]);

    return Array.from(ids)
      .map((id) => {
        const esperado =
          (parseInt(apertura[id], 10) || 0) +
          (parseInt(reposiciones[id], 10) || 0) -
          (parseInt(vendidos[id], 10) || 0);
        const fisico = parseInt(contado[id], 10) || 0;

        return {
          id: String(id),
          nombre: meta?.[String(id)]?.nombre || `Producto #${id}`,
          sku: meta?.[String(id)]?.sku || '',
          entregados: parseInt(apertura[id], 10) || 0,
          reposiciones: parseInt(reposiciones[id], 10) || 0,
          vendidos: parseInt(vendidos[id], 10) || 0,
          esperado,
          contado: fisico,
          diferencia: fisico - esperado,
        };
      })
      .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es'));
  }, [
    caja,
    pendingCloseTabacoDetalle,
    pendingCloseTabacoTotals,
    editingTabacoRevision,
    tabacoRevisionDetalleInput,
  ]);

  const diferenciaTabacoTotal = useMemo(
    () => revisionTabacoRows.reduce((sum, row) => sum + (Number(row.diferencia) || 0), 0),
    [revisionTabacoRows]
  );

  const resumenTabacoCierre = useMemo(
    () =>
      revisionTabacoRows.reduce(
        (acc, row) => {
          acc.entregados += Number(row.entregados) || 0;
          acc.reposiciones += Number(row.reposiciones) || 0;
          acc.vendidos += Number(row.vendidos) || 0;
          acc.totalAEntregar += Number(row.esperado) || 0;
          acc.contado += Number(row.contado) || 0;
          return acc;
        },
        {
          entregados: 0,
          reposiciones: 0,
          vendidos: 0,
          totalAEntregar: 0,
          contado: 0,
        }
      ),
    [revisionTabacoRows]
  );

  const iniciarCorreccionArqueoRevision = () => {
    if (isSubmitting) return;

    setArqueoRevisionCounts({ ...counts });
    setArqueoRevisionMediosInput(
      JSON.parse(JSON.stringify(confirmacionMediosInput || {}))
    );
    setEditingArqueoRevision(true);
  };

  const cancelarCorreccionArqueoRevision = () => {
    setArqueoRevisionCounts({});
    setArqueoRevisionMediosInput({});
    setEditingArqueoRevision(false);
  };

  const guardarCorreccionArqueoRevision = () => {
    const medioPendiente = confirmacionesMediosRevision.find(
      (medio) => medio.monto_reportado === null
    );

    if (medioPendiente) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: `Ingrese el total de cierre de ${medioPendiente.nombre}.`,
      });
      return;
    }

    setCounts({ ...arqueoRevisionCounts });
    setConfirmacionMediosInput(
      JSON.parse(JSON.stringify(arqueoRevisionMediosInput || {}))
    );
    setArqueoConfirmado(true);
    setArqueoRevisionCounts({});
    setArqueoRevisionMediosInput({});
    setEditingArqueoRevision(false);
    setSnackbar({ open: true, severity: 'success', message: 'Arqueo actualizado.' });
  };

  const iniciarCorreccionTabacoRevision = () => {
    if (isSubmitting || !pendingCloseTabacoDetalle) return;

    setTabacoRevisionDetalleInput({
      ...normalizarDetalleTabacoFrontend(pendingCloseTabacoDetalle),
    });
    setEditingTabacoRevision(true);
  };

  const actualizarConteoTabacoRevision = (productoId, value) => {
    const clean = String(value ?? '').replace(/\D/g, '');

    setTabacoRevisionDetalleInput((prev) => ({
      ...(prev || {}),
      [String(productoId)]: clean,
    }));
  };

  const cancelarCorreccionTabacoRevision = () => {
    setTabacoRevisionDetalleInput({});
    setEditingTabacoRevision(false);
  };

  const guardarCorreccionTabacoRevision = () => {
    const detalle = normalizarDetalleTabacoFrontend(tabacoRevisionDetalleInput || {});
    const productos = Array.isArray(pendingCloseTabacoTotals?.productos)
      ? pendingCloseTabacoTotals.productos
      : [];

    const preciosDisponibles = productos.some((producto) => {
      const precio = Number(
        producto?.precio_neto ?? producto?.precio ?? producto?.precio_bruto
      );
      return Number.isFinite(precio);
    });

    const totalMonetario = preciosDisponibles
      ? productos.reduce((sum, producto) => {
          const precio = Number(
            producto?.precio_neto ?? producto?.precio ?? producto?.precio_bruto ?? 0
          );
          const qty = Number(detalle?.[String(producto?.id)] || 0);
          return sum + (Number.isFinite(precio) ? precio * qty : 0);
        }, 0)
      : pendingCloseTabacoTotals?.total_monetario;

    setPendingCloseTabacoDetalle(detalle);
    setPendingCloseTabacoTotals((prev) => ({
      ...(prev || {}),
      total_productos_recibidos: sumarDetalleTabacoFrontend(detalle),
      total_productos: Object.keys(detalle).length,
      total_monetario: totalMonetario,
      productos,
    }));

    setTabacoRevisionDetalleInput({});
    setEditingTabacoRevision(false);
    setSnackbar({ open: true, severity: 'success', message: 'Conteo de cigarrillos actualizado.' });
  };

  const clearPdf = useCallback(() => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    onCloseModal?.();
  }, [pdfUrl, onCloseModal]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  const downloadPdf = async (cierreId) => {
    try {
      const resp = await api.get(`/impresion_cierrecaja/${cierreId}/80mm`, {
        responseType: 'blob',
      });

      setPdfUrl(URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })));
      setShowArqueo(false);
    } catch {
      setSnackbar({
        open: true,
        message: 'Error descargando comprobante',
        severity: 'error',
      });
    }
  };

  const handleIncrement = (val) => {
    setArqueoConfirmado(false);
    setCounts((prev) => ({
      ...prev,
      [val]: (clampInt(prev[val]) || 0) + 1,
    }));
  };

  const handleDecrement = (val) => {
    setArqueoConfirmado(false);
    setCounts((prev) => {
      const next = Math.max(0, (clampInt(prev[val]) || 0) - 1);
      return { ...prev, [val]: next || '' };
    });
  };

  const handleResetArqueo = () => {
    setArqueoConfirmado(false);
    setCounts({});
  };

  const resetProcesoTabacoApertura = () => {
    setTabacoModalOpen(false);
    setNextAction(null);
    setPendingMonto(null);
    setUsarCierreAnteriorTabaco(false);
    setUltimoCierreTabaco(null);
    setTabacoBaseApertura({});
  };

  const getMontoInicialValidado = () => {
    if (String(montoInicialStr || '').trim() === '') {
      setSnackbar({
        open: true,
        message: 'Ingrese un monto inicial',
        severity: 'error',
      });
      return null;
    }

    const monto = parseCLPInput(montoInicialStr);

    if (!Number.isFinite(monto) || monto < 0) {
      setSnackbar({
        open: true,
        message: 'Monto inicial inválido',
        severity: 'error',
      });
      return null;
    }

    return monto;
  };

  const cargarUltimoCierreTabaco = async () => {
    setLoadingUltimoCierreTabaco(true);

    try {
      const { data } = await api.get('/pos_cajas/ultimo-cierre-tabaco');

      const detalle = normalizarDetalleTabacoFrontend(data?.detalle_tabaco_cierre || {});
      const detalleApertura = filtrarDetalleTabacoPositivoFrontend(detalle);
      const productosOmitidosEnCero = Math.max(
        0,
        Object.keys(detalle).length - Object.keys(detalleApertura).length
      );

      const base = {
        disponible: !!data?.disponible,
        caja_id: data?.caja_id || null,
        fecha_cierre: data?.fecha_cierre || null,

        // Se conserva el cierre completo sólo como referencia histórica.
        detalle_tabaco_cierre: detalle,

        // Para una nueva apertura sólo se heredan productos con existencia > 0.
        detalle_tabaco_apertura: detalleApertura,
        productos_omitidos_cero: productosOmitidosEnCero,
        total_cigarrillos_cierre: Number(data?.total_cigarrillos_cierre || 0),
      };

      setUltimoCierreTabaco(base);
      setTabacoBaseApertura(base.disponible ? detalleApertura : {});

      if (!base.disponible) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: 'No existe un cierre anterior de cigarrillos. Se iniciará en blanco.',
        });
      }

      return base;
    } catch (err) {
      console.error('Error cargando último cierre de tabaco:', err);

      setUltimoCierreTabaco(null);
      setTabacoBaseApertura({});

      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'No se pudo cargar el último cierre de cigarrillos. Se iniciará en blanco.',
      });

      return null;
    } finally {
      setLoadingUltimoCierreTabaco(false);
    }
  };

  const prepararAperturaConTabaco = async (usarCierreAnterior = false) => {
    const monto = getMontoInicialValidado();
    if (monto === null) return;

    setPendingMonto(monto);
    setNextAction('abrir');
    setUsarCierreAnteriorTabaco(usarCierreAnterior);

    if (usarCierreAnterior) {
      const base = await cargarUltimoCierreTabaco();
      setTabacoBaseApertura(
        base?.disponible
          ? (base.detalle_tabaco_apertura || filtrarDetalleTabacoPositivoFrontend(base.detalle_tabaco_cierre || {}))
          : {}
      );
    } else {
      setUltimoCierreTabaco(null);
      setTabacoBaseApertura({});
    }

    setTabacoModalOpen(true);
  };

  useEffect(() => {
    if (!open) return;

    setIsLoading(true);
    setIsAbierta(false);
    setCajaData(null);
    setMontoInicialStr('');
    setCounts({});
    setSnackbar({ open: false, message: '', severity: 'success' });
    setPdfUrl(null);
    setShowArqueo(true);
    setNextAction(null);
    setTabacoModalOpen(false);
    setPinModalOpen(false);
    setPinPurpose(null);
    pinAuthorizationTokenRef.current = null;
    localStorage.removeItem('pinToken');

    setUsarCierreAnteriorTabaco(false);
    setUltimoCierreTabaco(null);
    setTabacoBaseApertura({});
    setLoadingUltimoCierreTabaco(false);
    setPendingMonto(null);
    setConfirmacionMediosInput({});
    setResumenMovimientosManuales({ ingresos: 0, egresos: 0, cargado: false });
    setArqueoConfirmado(false);
    setSupervisorWaitingOpen(false);
    setSupervisorReviewOpen(false);
    setPendingCloseTabacoDetalle(null);
    setPendingCloseTabacoTotals(null);
    setEditingArqueoRevision(false);
    setEditingTabacoRevision(false);
    setArqueoRevisionCounts({});
    setArqueoRevisionMediosInput({});
    setTabacoRevisionDetalleInput({});
    setCierreDraftReadyKey(null);
    cierreDraftClosedRef.current = null;

    api
      .get('/pos_cajas/estado')
      .then(({ data }) => {
        if (!data?.abierta) {
          setIsAbierta(false);
          setCajaData(null);
          return null;
        }

        const estadoCaja = normalizeCajaResponse(data);

        setIsAbierta(true);
        setCajaData(estadoCaja);

        const cajaId = estadoCaja?.id;

        if (!cajaId) {
          return null;
        }

        return api
          .get(`/pos_cajas/${cajaId}/estado`)
          .then(({ data: detalle }) => {
            const detalleCaja = normalizeCajaResponse(detalle);

            /*
             * Fusionamos en vez de reemplazar. /pos_cajas/estado contiene
             * agregados del turno (ventas_transbank, saldos teóricos, etc.)
             * y /:id/estado aporta el detalle de la caja.
             */
            setCajaData({
              ...(estadoCaja || {}),
              ...(detalleCaja || {}),
            });
          })
          .catch((err) => {
            console.error('Error cargando detalle de caja:', err);

            setCajaData(estadoCaja);

            setSnackbar({
              open: true,
              message: 'No se pudo cargar el detalle completo. Se usará el estado disponible.',
              severity: 'warning',
            });
          });
      })
      .catch((err) => {
        console.error('Error cargando estado de caja:', err);

        setSnackbar({
          open: true,
          message: 'Error cargando estado',
          severity: 'error',
        });
      })
      .finally(() => setIsLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open || !isAbierta || !caja?.id) return undefined;

    const cajaId = String(caja.id);
    if (String(cierreDraftClosedRef.current || '') === cajaId) return undefined;

    const key = getCierreCajaDraftKey(caja.id);
    if (cierreDraftReadyKey === key) return undefined;

    const draft = readLocalJson(key);

    if (draft && String(draft.cajaId || '') === cajaId) {
      if (draft.counts && typeof draft.counts === 'object') {
        setCounts(draft.counts);
      }

      if (draft.confirmacionMediosInput && typeof draft.confirmacionMediosInput === 'object') {
        setConfirmacionMediosInput(draft.confirmacionMediosInput);
      }

      setArqueoConfirmado(Boolean(draft.arqueoConfirmado));

      if (draft.pendingCloseTabacoDetalle && typeof draft.pendingCloseTabacoDetalle === 'object') {
        setPendingCloseTabacoDetalle(draft.pendingCloseTabacoDetalle);
      }

      if (draft.pendingCloseTabacoTotals && typeof draft.pendingCloseTabacoTotals === 'object') {
        setPendingCloseTabacoTotals(draft.pendingCloseTabacoTotals);
      }

      if (draft.phase === 'supervisor_waiting' || draft.phase === 'review') {
        setTabacoModalOpen(false);
        setNextAction(null);
        setPinModalOpen(false);
        setPinPurpose(null);
        setSupervisorReviewOpen(false);
        setSupervisorWaitingOpen(true);
        pinAuthorizationTokenRef.current = null;
        localStorage.removeItem('pinToken');
      }

      setSnackbar({
        open: true,
        message: 'Cierre recuperado.',
        severity: 'info',
      });
    }

    setCierreDraftReadyKey(key);
    return undefined;
  }, [open, isAbierta, caja?.id, cierreDraftReadyKey]);

  useEffect(() => {
    if (!open || !isAbierta || !caja?.id) return undefined;

    const cajaId = String(caja.id);
    if (String(cierreDraftClosedRef.current || '') === cajaId) return undefined;

    const key = getCierreCajaDraftKey(caja.id);
    if (cierreDraftReadyKey !== key) return undefined;

    const phase = supervisorReviewOpen
      ? 'review'
      : supervisorWaitingOpen
        ? 'supervisor_waiting'
        : 'capture';

    writeLocalJson(key, {
      version: 1,
      cajaId: caja.id,
      tenantid: getTenantScopeFrontend(),
      updatedAt: new Date().toISOString(),
      counts,
      confirmacionMediosInput,
      arqueoConfirmado: Boolean(arqueoConfirmado),
      pendingCloseTabacoDetalle: pendingCloseTabacoDetalle || null,
      pendingCloseTabacoTotals: pendingCloseTabacoTotals || null,
      phase,
    });

    return undefined;
  }, [
    open,
    isAbierta,
    caja?.id,
    cierreDraftReadyKey,
    counts,
    confirmacionMediosInput,
    arqueoConfirmado,
    pendingCloseTabacoDetalle,
    pendingCloseTabacoTotals,
    supervisorWaitingOpen,
    supervisorReviewOpen,
  ]);

  useEffect(() => {
    if (!open || !isAbierta || !caja?.id) {
      setResumenMovimientosManuales({ ingresos: 0, egresos: 0, cargado: false });
      return undefined;
    }

    let mounted = true;

    api
      .get('/pos_cajas/movimientos')
      .then(({ data }) => {
        if (!mounted) return;

        const resumen = resumirMovimientosManuales(data);
        setResumenMovimientosManuales({ ...resumen, cargado: true });
      })
      .catch((err) => {
        console.error('Error cargando movimientos manuales de caja:', err);

        if (!mounted) return;

        setResumenMovimientosManuales({ ingresos: 0, egresos: 0, cargado: false });
      });

    return () => {
      mounted = false;
    };
  }, [open, isAbierta, caja?.id]);

  const abrirCaja = async (montoInicialAbrir, detalleTabaco = null, options = {}) => {
    setIsSubmitting(true);

    try {
      const usarCierreAnterior = !!options.usarCierreAnterior;

      const body = {
        monto_inicial: montoInicialAbrir,
        usar_tabaco_cierre_anterior: usarCierreAnterior,
      };

      const detalleNormalizado = normalizarDetalleTabacoFrontend(detalleTabaco || {});

      if (hasDetalleTabaco(detalleNormalizado)) {
        body.detalle_tabaco_entrega = detalleNormalizado;
      }

      const { data } = await api.post('/pos_cajas/abrir', body);
      const cajaAbierta = normalizeCajaResponse(data);

      setSnackbar({
        open: true,
        message: usarCierreAnterior
          ? 'Caja abierta usando cierre anterior de cigarrillos'
          : hasDetalleTabaco(detalleNormalizado)
            ? 'Caja abierta con conteo manual de cigarrillos'
            : 'Caja abierta',
        severity: 'success',
      });

      setIsAbierta(true);
      setCajaData(cajaAbierta);
      resetProcesoTabacoApertura();
      onCloseModal?.();
      window.dispatchEvent(new Event('cajaActualizada'));
    } catch (err) {
      setSupervisorReviewOpen(false);
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          'Error al abrir caja',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const solicitarPin = (purpose) => {
    pinAuthorizationTokenRef.current = null;
    localStorage.removeItem('pinToken');
    setPinPurpose(purpose);
    setPinModalOpen(true);
  };

  const cerrarCaja = async (detalleTabaco, totals) => {
    if (!caja?.id) {
      setSnackbar({
        open: true,
        message: 'No se pudo identificar la caja abierta.',
        severity: 'error',
      });
      return;
    }

    const pinToken =
      pinAuthorizationTokenRef.current ||
      localStorage.getItem('pinToken');

    if (!pinToken) {
      solicitarPin('close');
      return;
    }

    setIsSubmitting(true);

    try {
      const detalleNormalizado = normalizarDetalleTabacoFrontend(detalleTabaco || {});

      const body = {
        monto_efectivo_real: totalArqueo,
        detalle_arqueo: counts,
      };

      if (confirmacionesMedios.length > 0) {
        const faltante = confirmacionesMedios.find(
          (medio) => medio.monto_reportado === null
        );

        if (faltante) {
          throw new Error(`Ingresa el total de cierre de ${faltante.nombre}.`);
        }

        body.confirmacion_medios = Object.fromEntries(
          confirmacionesMedios.map((medio) => [
            medio.clave,
            {
              monto_real: medio.monto_reportado,
              referencia: medio.referencia || null,
            },
          ])
        );
      }

      if (hasDetalleTabaco(detalleNormalizado)) {
        body.detalle_tabaco_cierre = detalleNormalizado;
        body.total_productos_recibidos = totals?.total_productos_recibidos;
        body.total_productos = totals?.total_productos;
        body.total_monetario = totals?.total_monetario;
      }

      const { data } = await api.post(
        `/pos_cajas/${caja.id}/cerrar`,
        body,
        {
          headers: pinToken
            ? { 'X-PIN-Token': pinToken }
            : {},
        }
      );

      setSnackbar({
        open: true,
        message: data?.mensaje || 'Caja cerrada',
        severity: 'success',
      });

      cierreDraftClosedRef.current = String(caja.id);
      clearCierreCajaDrafts(caja.id);

      setSupervisorWaitingOpen(false);
      setSupervisorReviewOpen(false);
      setPendingCloseTabacoDetalle(null);
      setPendingCloseTabacoTotals(null);
      setEditingArqueoRevision(false);
      setEditingTabacoRevision(false);
      setArqueoRevisionCounts({});
      setArqueoRevisionMediosInput({});
      setTabacoRevisionDetalleInput({});
      setArqueoConfirmado(false);

      window.dispatchEvent(new Event('cajaActualizada'));

      downloadPdf(data?.caja?.id || data?.id || caja.id);
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          'Error al cerrar caja',
        severity: 'error',
      });
    } finally {
      // La autorización PIN es de un solo uso para este intento de cierre.
      pinAuthorizationTokenRef.current = null;
      localStorage.removeItem('pinToken');
      setIsSubmitting(false);
    }
  };

  const prepararRevisionFinal = (detalleTabaco = null, totals = null) => {
    setPendingCloseTabacoDetalle(detalleTabaco);
    setPendingCloseTabacoTotals(totals);
    setNextAction(null);
    setSupervisorReviewOpen(false);
    setSupervisorWaitingOpen(true);
    setPinModalOpen(false);
    setPinPurpose(null);
    pinAuthorizationTokenRef.current = null;
    localStorage.removeItem('pinToken');
  };

  const solicitarRevisionSupervisor = () => {
    if (isSubmitting) return;
    solicitarPin('review');
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (action !== 'cerrar') return;

    if (!arqueoConfirmado) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'Confirma que terminaste el conteo físico del efectivo antes de continuar.',
      });
      return;
    }

    const medioPendiente = confirmacionesMedios.find(
      (medio) => medio.monto_reportado === null
    );

    if (medioPendiente) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: `Ingresa el total de cierre de ${medioPendiente.nombre}.`,
      });
      return;
    }

    if (requiereConteoTabacoCierre) {
      setNextAction('cerrar');
      setTabacoModalOpen(true);
      return;
    }

    prepararRevisionFinal(null, null);
  };

  const handleCancelarConteoTabaco = () => {
    if (nextAction === 'cerrar') {
      setTabacoModalOpen(false);
      setNextAction(null);
      return;
    }

    resetProcesoTabacoApertura();
  };

  const handleTabacoConfirm = (detalleTabaco, totals) => {
    setTabacoModalOpen(false);

    if (nextAction === 'abrir') {
      return abrirCaja(pendingMonto, detalleTabaco, {
        usarCierreAnterior: usarCierreAnteriorTabaco,
      });
    }

    prepararRevisionFinal(detalleTabaco, totals);
    return undefined;
  };

  const title = action === 'abrir' ? 'Abrir Caja' : `Cerrar Caja #${caja?.id ?? ''}`;

  return (
    <>
      <Dialog
        open={open && showArqueo && !supervisorWaitingOpen && !supervisorReviewOpen}
        onClose={!tabacoModalOpen ? onCloseModal : undefined}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <Typography variant="h6" fontWeight={900}>
              {title}
            </Typography>

          </Box>

          <IconButton onClick={!tabacoModalOpen ? onCloseModal : undefined} aria-label="Cerrar modal">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        {isLoading ? (
          <DialogContent
            sx={{
              height: 240,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </DialogContent>
        ) : (
          <DialogContent>
            <form ref={formRef} onSubmit={handleSubmit}>
              {isAbierta && action === 'cerrar' ? (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={900}>
                          Etapa 1 de 3 · Arqueo y comprobantes
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ingrese el efectivo contado y los totales informados por la máquina de pago.
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={`Total: ${fmtCLP(totalArqueo)}`}
                        sx={{ fontWeight: 800 }}
                      />
                    </Stack>
                  </Paper>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" fontWeight={900}>
                        Billetes
                      </Typography>
                      <Tooltip title="Limpiar todos los conteos">
                        <span>
                          <Button
                            size="small"
                            startIcon={<ResetIcon />}
                            onClick={handleResetArqueo}
                            disabled={isSubmitting}
                          >
                            Limpiar
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(5, 108px)', md: 'repeat(5, 1fr)' },
                        gap: 1,
                        overflowX: { xs: 'auto', md: 'visible' },
                        pb: 0.5,
                      }}
                    >
                      {BILLS.map(({ value, label, img }) => {
                        const cnt = clampInt(counts[value]);
                        const subtotal = cnt * value;

                        return (
                          <Paper
                            key={value}
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              p: 0.75,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.75,
                              minWidth: { xs: 108, md: 'auto' },
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                ${label}
                              </Typography>
                              <Typography variant="caption" fontWeight={800}>
                                {fmtCLP(subtotal)}
                              </Typography>
                            </Stack>

                            <Box
                              onClick={() => handleIncrement(value)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => (e.key === 'Enter' ? handleIncrement(value) : null)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                borderRadius: 1.5,
                                py: 0.25,
                              }}
                              title="Click para +1"
                            >
                              <img
                                src={img}
                                alt={label}
                                style={{
                                  width: '100%',
                                  maxWidth: 86,
                                  maxHeight: 42,
                                  objectFit: 'contain',
                                }}
                              />
                            </Box>

                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <TextField
                                size="small"
                                value={counts[value] ?? ''}
                                onChange={(e) => {
                                  setArqueoConfirmado(false);
                                  setCounts((prev) => ({
                                    ...prev,
                                    [value]: e.target.value.replace(/\D/g, ''),
                                  }));
                                }}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                sx={{ width: 64 }}
                                placeholder="0"
                              />

                              <Tooltip title="Restar 1">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDecrement(value)}
                                    disabled={isSubmitting || cnt <= 0}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Box>

                    <Typography variant="subtitle2" fontWeight={900} sx={{ mt: 0.5 }}>
                      Monedas
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(4, 108px)', md: 'repeat(4, 1fr)' },
                        gap: 1,
                        overflowX: { xs: 'auto', md: 'visible' },
                        pb: 0.5,
                      }}
                    >
                      {COINS.map(({ value, label, img }) => {
                        const cnt = clampInt(counts[value]);
                        const subtotal = cnt * value;

                        return (
                          <Paper
                            key={value}
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              p: 0.75,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.75,
                              minWidth: { xs: 108, md: 'auto' },
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                ${label}
                              </Typography>
                              <Typography variant="caption" fontWeight={800}>
                                {fmtCLP(subtotal)}
                              </Typography>
                            </Stack>

                            <Box
                              onClick={() => handleIncrement(value)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => (e.key === 'Enter' ? handleIncrement(value) : null)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                borderRadius: 1.5,
                                py: 0.25,
                              }}
                              title="Click para +1"
                            >
                              <img
                                src={img}
                                alt={label}
                                style={{
                                  width: '100%',
                                  maxWidth: 78,
                                  maxHeight: 38,
                                  objectFit: 'contain',
                                }}
                              />
                            </Box>

                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <TextField
                                size="small"
                                value={counts[value] ?? ''}
                                onChange={(e) => {
                                  setArqueoConfirmado(false);
                                  setCounts((prev) => ({
                                    ...prev,
                                    [value]: e.target.value.replace(/\D/g, ''),
                                  }));
                                }}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                sx={{ width: 64 }}
                                placeholder="0"
                              />

                              <Tooltip title="Restar 1">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDecrement(value)}
                                    disabled={isSubmitting || cnt <= 0}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderColor: arqueoConfirmado ? 'success.main' : 'divider',
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            TOTAL CONTADO FÍSICAMENTE
                          </Typography>
                          <Typography variant="h5" fontWeight={900}>
                            {fmtCLP(totalArqueo)}
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={arqueoConfirmado}
                              onChange={(e) => setArqueoConfirmado(e.target.checked)}
                              disabled={isSubmitting}
                            />
                          }
                          label="Conteo finalizado"
                        />
                      </Stack>
                    </Stack>
                  </Paper>

                  {mediosCierre.length > 0 && (
                    <Stack spacing={1.25}>
                      {mediosCierre.map((medio) => {
                        const entrada = confirmacionMediosInput?.[medio.clave] || {};

                        return (
                          <Paper
                            key={medio.clave}
                            variant="outlined"
                            sx={{ p: 1.5, borderRadius: 2 }}
                          >
                            <Stack spacing={1.25}>
                              <Typography variant="subtitle2" fontWeight={900}>
                                {medio.nombre}
                              </Typography>

                              <Grid container spacing={1.5}>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Total según voucher"
                                    value={entrada.monto || ''}
                                    onChange={(e) =>
                                      setConfirmacionMediosInput((prev) => ({
                                        ...prev,
                                        [medio.clave]: {
                                          ...(prev?.[medio.clave] || {}),
                                          monto: formatCLPInput(e.target.value),
                                        },
                                      }))
                                    }
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    disabled={isSubmitting}
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="N° voucher / lote"
                                    value={entrada.referencia || ''}
                                    onChange={(e) =>
                                      setConfirmacionMediosInput((prev) => ({
                                        ...prev,
                                        [medio.clave]: {
                                          ...(prev?.[medio.clave] || {}),
                                          referencia: e.target.value,
                                        },
                                      }))
                                    }
                                    placeholder="Referencia del comprobante"
                                    disabled={isSubmitting}
                                  />
                                </Grid>
                              </Grid>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}

                  {requiereConteoTabacoCierre && (
                    <Alert severity="info" icon={<InventoryIcon />}>
                      Al continuar, realice el conteo de cigarrillos.
                    </Alert>
                  )}
                </Stack>
              ) : (
                <Box textAlign="center" py={4}>
                  <MoneyIcon fontSize="large" />

                  <Typography variant="h6" gutterBottom fontWeight={900}>
                    Abrir Caja
                  </Typography>

                  <TextField
                    label="Monto Inicial"
                    value={montoInicialStr}
                    onChange={(e) => setMontoInicialStr(formatCLPInput(e.target.value))}
                    disabled={isSubmitting || loadingUltimoCierreTabaco}
                    inputProps={{ inputMode: 'numeric' }}
                  />

                  {ultimoCierreTabaco?.disponible && (
                    <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                      Cierre anterior de cigarrillos disponible.
                    </Alert>
                  )}
                </Box>
              )}
            </form>
          </DialogContent>
        )}

        <DialogActions sx={{ justifyContent: 'flex-end', gap: 1.25, p: 2 }}>
          {action === 'abrir' ? (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<InventoryIcon />}
                onClick={() => prepararAperturaConTabaco(false)}
                disabled={isSubmitting || loadingUltimoCierreTabaco || !montoInicialStr}
              >
                Cigarrillos en blanco
              </Button>

              <Button
                variant="contained"
                color="primary"
                startIcon={
                  loadingUltimoCierreTabaco ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <HistoryIcon />
                  )
                }
                onClick={() => prepararAperturaConTabaco(true)}
                disabled={isSubmitting || loadingUltimoCierreTabaco || !montoInicialStr}
              >
                Usar cierre anterior
              </Button>

              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  const monto = getMontoInicialValidado();
                  if (monto === null) return;

                  abrirCaja(monto, null, {
                    usarCierreAnterior: false,
                  });
                }}
                disabled={isSubmitting || loadingUltimoCierreTabaco || !montoInicialStr}
              >
                Abrir normal
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onCloseModal} disabled={isSubmitting}>
                Volver
              </Button>

              <Tooltip
                title={
                  !arqueoConfirmado
                    ? 'Finaliza el conteo'
                    : !mediosCierreCapturados
                      ? 'Completa los medios de pago'
                      : 'Continuar'
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !canClose}
                    onClick={() => {
                      formRef.current?.dispatchEvent(
                        new Event('submit', { cancelable: true, bubbles: true })
                      );
                    }}
                  >
                    {isSubmitting
                      ? <CircularProgress size={20} color="inherit" />
                      : 'Continuar'}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={supervisorWaitingOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={900}>
            Esperando supervisor
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Arqueo finalizado.
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ py: 1 }}>
            <Alert severity="info">
              Solicite la presencia de un supervisor para continuar.
            </Alert>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">Efectivo contado</Typography>
                  <Typography variant="body2" fontWeight={900}>{fmtCLP(totalArqueo)}</Typography>
                </Stack>

                {confirmacionesMedios.map((medio) => (
                  <Stack key={medio.clave} direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">{medio.nombre}</Typography>
                    <Typography variant="body2" fontWeight={900}>{fmtCLP(medio.monto_reportado)}</Typography>
                  </Stack>
                ))}

                {requiereConteoTabacoCierre && pendingCloseTabacoDetalle && (
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">Cigarrillos contados</Typography>
                    <Typography variant="body2" fontWeight={900}>
                      {sumarDetalleTabacoFrontend(pendingCloseTabacoDetalle)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>

            
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={solicitarRevisionSupervisor}
            disabled={isSubmitting || pinModalOpen}
          >
            Autorizar revisión
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <PinConfirmModal
        open={pinModalOpen}
        title={
          pinPurpose === 'close'
            ? 'Confirmar cierre de caja'
            : 'Autorizar revisión'
        }
        message={
          pinPurpose === 'close'
            ? 'Ingrese el PIN para cerrar la caja.'
            : 'Ingrese el PIN para revisar el cierre.'
        }
        onClose={() => {
          setPinModalOpen(false);
          setPinPurpose(null);

          if (!pinAuthorizationTokenRef.current) {
            localStorage.removeItem('pinToken');
          }
        }}
        onVerified={(pinData) => {
          const currentPurpose = pinPurpose;
          const token =
            pinData?.token ||
            localStorage.getItem('pinToken') ||
            null;

          if (currentPurpose === 'review') {
            pinAuthorizationTokenRef.current = null;
            localStorage.removeItem('pinToken');
            setPinModalOpen(false);
            setPinPurpose(null);
            setSupervisorWaitingOpen(false);
            setEditingArqueoRevision(false);
            setEditingTabacoRevision(false);
            setArqueoRevisionCounts({});
            setArqueoRevisionMediosInput({});
            setTabacoRevisionDetalleInput({});
            setSupervisorReviewOpen(true);
            return;
          }

          if (currentPurpose === 'close') {
            if (!token) {
              setSnackbar({
                open: true,
                message: 'Para cerrar la caja se requiere una autorización PIN online válida.',
                severity: 'error',
              });
              setPinModalOpen(false);
              setPinPurpose(null);
              return;
            }

            pinAuthorizationTokenRef.current = token;
            setPinModalOpen(false);
            setPinPurpose(null);
            cerrarCaja(pendingCloseTabacoDetalle, pendingCloseTabacoTotals);
          }
        }}
      />

      <Dialog
        open={supervisorReviewOpen}
        onClose={isSubmitting || editingArqueoRevision || editingTabacoRevision ? undefined : () => {
          setSupervisorReviewOpen(false);
          setSupervisorWaitingOpen(true);
          pinAuthorizationTokenRef.current = null;
          localStorage.removeItem('pinToken');
          setPinPurpose(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={900}>
            Revisión final de cierre
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Verifique los valores antes de cerrar la caja.
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 0 }}>
          <Stack spacing={2} sx={{ pt: 2.5 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              spacing={1}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={900}>
                  Arqueo
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Revise el efectivo y los comprobantes.
                </Typography>
              </Box>

              {editingArqueoRevision ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={cancelarCorreccionArqueoRevision}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={guardarCorreccionArqueoRevision}
                    disabled={isSubmitting}
                  >
                    Guardar correcciones
                  </Button>
                </Stack>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={iniciarCorreccionArqueoRevision}
                  disabled={isSubmitting || editingTabacoRevision}
                >
                  Corregir arqueo
                </Button>
              )}
            </Stack>

            <Alert severity={diffRevision === 0 ? 'success' : 'warning'}>
              <Typography variant="subtitle2" fontWeight={900}>
                {diffRevision === 0 ? 'Efectivo cuadrado' : diffRevision < 0 ? 'Faltante de efectivo' : 'Sobrante de efectivo'}
              </Typography>
            </Alert>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <StatCard icon={<SaleIcon />} title="Efectivo teórico" value={fmtCLP(efectivoTeorico)}  />
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard icon={<MoneyIcon />} title="Efectivo contado" value={fmtCLP(totalArqueoRevision)}  />
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard
                  icon={<BalanceIcon />}
                  title="Diferencia"
                  value={fmtCLP(diffRevision)}
                  sub={diffRevision === 0 ? 'Sin diferencia' : diffRevision < 0 ? 'Faltante' : 'Sobrante'}
                />
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                Composición del efectivo teórico
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Fondo inicial</Typography><Typography fontWeight={800}>{fmtCLP(montoInicial)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Ventas efectivo</Typography><Typography fontWeight={800}>{fmtCLP(ventasEfectivo)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Ingresos manuales</Typography><Typography fontWeight={800}>{fmtCLP(ingresosManuales)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Retiros manuales</Typography><Typography fontWeight={800}>{fmtCLP(retirosManuales)}</Typography></Grid>
              </Grid>
            </Paper>

            {editingArqueoRevision && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={900}>
                      Efectivo contado
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Corrija las cantidades por denominación.
                    </Typography>
                  </Box>

                  <Grid container spacing={1.25}>
                    {DENOMINATIONS.map(({ value, label }) => {
                      const cnt = clampInt(arqueoRevisionCounts[value]);
                      return (
                        <Grid item xs={6} sm={4} md={3} key={`revision-${value}`}>
                          <TextField
                            fullWidth
                            size="small"
                            label={`$${label}`}
                            value={arqueoRevisionCounts[value] ?? ''}
                            onChange={(e) => {
                              const clean = String(e.target.value || '').replace(/\D/g, '');
                              setArqueoRevisionCounts((prev) => ({
                                ...prev,
                                [value]: clean === '' ? '' : clampInt(clean),
                              }));
                            }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 0 }}
                            helperText={`${cnt} × ${fmtCLP(value)} = ${fmtCLP(cnt * value)}`}
                            disabled={isSubmitting}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
                      <Typography variant="body2" fontWeight={800}>
                        Total contado
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {fmtCLP(totalArqueoRevision)}
                      </Typography>
                    </Stack>
                  </Paper>

                  {mediosCierre.length > 0 && (
                    <Stack spacing={1.25}>
                      {mediosCierre.map((medio) => {
                        const entrada = arqueoRevisionMediosInput?.[medio.clave] || {};
                        return (
                          <Paper key={`revision-edit-${medio.clave}`} variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: 'background.paper' }}>
                            <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                              {medio.nombre}
                            </Typography>
                            <Grid container spacing={1.25}>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Total según voucher"
                                  value={entrada.monto || ''}
                                  onChange={(e) =>
                                    setArqueoRevisionMediosInput((prev) => ({
                                      ...prev,
                                      [medio.clave]: {
                                        ...(prev?.[medio.clave] || {}),
                                        monto: formatCLPInput(e.target.value),
                                      },
                                    }))
                                  }
                                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                  disabled={isSubmitting}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="N° voucher / lote"
                                  value={entrada.referencia || ''}
                                  onChange={(e) =>
                                    setArqueoRevisionMediosInput((prev) => ({
                                      ...prev,
                                      [medio.clave]: {
                                        ...(prev?.[medio.clave] || {}),
                                        referencia: e.target.value,
                                      },
                                    }))
                                  }
                                  disabled={isSubmitting}
                                />
                              </Grid>
                            </Grid>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            )}

            {confirmacionesMediosRevision.length > 0 && (
              <Stack spacing={1.25}>
                {confirmacionesMediosRevision.map((medio) => (
                  <Paper
                    key={medio.clave}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                      {medio.nombre}
                    </Typography>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={4}>
                        <StatCard
                          icon={<CardIcon />}
                          title="Sistema POS"
                          value={fmtCLP(medio.total)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <StatCard
                          icon={<CardIcon />}
                          title="Voucher informado"
                          value={fmtCLP(medio.monto_reportado)}
                          sub={medio.referencia || 'Sin referencia'}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <StatCard
                          icon={<BalanceIcon />}
                          title="Diferencia"
                          value={fmtCLP(medio.diferencia)}
                        />
                      </Grid>
                    </Grid>

                    {medio.diferencia !== 0 && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                        Existe una diferencia en este medio de pago.
                      </Alert>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}

            {revisionTabacoRows.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={900}>
                      Cigarrillos
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Entregados + reposiciones − ventas = total a entregar.
                    </Typography>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <Chip
                      size="small"
                      color={diferenciaTabacoTotal === 0 ? 'success' : 'warning'}
                      label={`Diferencia: ${diferenciaTabacoTotal > 0 ? '+' : ''}${diferenciaTabacoTotal}`}
                    />

                    {editingTabacoRevision ? (
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          color="inherit"
                          onClick={cancelarCorreccionTabacoRevision}
                          disabled={isSubmitting}
                        >
                          Cancelar
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={guardarCorreccionTabacoRevision}
                          disabled={isSubmitting}
                        >
                          Guardar correcciones
                        </Button>
                      </Stack>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={iniciarCorreccionTabacoRevision}
                        disabled={isSubmitting || editingArqueoRevision}
                      >
                        Corregir conteo
                      </Button>
                    )}
                  </Stack>
                </Stack>

                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                  <Grid item xs={6} md={2.4}>
                    <StatCard icon={<InventoryIcon />} title="Entregados" value={String(resumenTabacoCierre.entregados)} />
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <StatCard icon={<InventoryIcon />} title="Reposiciones" value={String(resumenTabacoCierre.reposiciones)} />
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <StatCard icon={<SaleIcon />} title="Ventas" value={String(resumenTabacoCierre.vendidos)} />
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <StatCard icon={<BalanceIcon />} title="Total a entregar" value={String(resumenTabacoCierre.totalAEntregar)} />
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <StatCard icon={<InventoryIcon />} title="Conteo físico" value={String(resumenTabacoCierre.contado)} />
                  </Grid>
                </Grid>

                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 900, zIndex: 3 }}>Producto</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 900, zIndex: 3 }}>SKU</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', fontWeight: 900, zIndex: 3 }}>Entregados</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', fontWeight: 900, zIndex: 3 }}>Repos.</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', fontWeight: 900, zIndex: 3 }}>Ventas</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', fontWeight: 900, zIndex: 3 }}>Total a entregar</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', fontWeight: 900, zIndex: 3 }}>Contado</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', fontWeight: 900, zIndex: 3 }}>Dif.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {revisionTabacoRows.map((row) => (
                      <TableRow key={row.id} sx={row.diferencia !== 0 ? { bgcolor: 'action.hover' } : undefined}>
                        <TableCell>{row.nombre}</TableCell>
                        <TableCell>{row.sku || '—'}</TableCell>
                        <TableCell align="right">{row.entregados}</TableCell>
                        <TableCell align="right">{row.reposiciones}</TableCell>
                        <TableCell align="right">{row.vendidos}</TableCell>
                        <TableCell align="right"><strong>{row.esperado}</strong></TableCell>
                        <TableCell align="right">
                          {editingTabacoRevision ? (
                            <TextField
                              size="small"
                              value={tabacoRevisionDetalleInput?.[String(row.id)] ?? 0}
                              onChange={(e) => actualizarConteoTabacoRevision(row.id, e.target.value)}
                              inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                min: 0,
                                style: { textAlign: 'right', width: 54 },
                              }}
                              disabled={isSubmitting}
                              sx={{ width: 88 }}
                            />
                          ) : (
                            row.contado
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            color={row.diferencia === 0 ? 'success' : 'warning'}
                            variant={row.diferencia === 0 ? 'outlined' : 'filled'}
                            label={`${row.diferencia > 0 ? '+' : ''}${row.diferencia}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Alert
                  severity={diferenciaTabacoTotal === 0 ? 'success' : 'warning'}
                  sx={{ mt: 1.5 }}
                >
                  {diferenciaTabacoTotal === 0
                    ? `Conteo correcto: ${resumenTabacoCierre.totalAEntregar} unidad(es) a entregar.`
                    : `Diferencia de ${diferenciaTabacoTotal > 0 ? '+' : ''}${diferenciaTabacoTotal} unidad(es). Revise el conteo.`}
                </Alert>
              </Paper>
            )}

          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSupervisorReviewOpen(false);
              setSupervisorWaitingOpen(true);
              setPinPurpose(null);
              pinAuthorizationTokenRef.current = null;
              localStorage.removeItem('pinToken');
            }}
            disabled={isSubmitting || editingArqueoRevision || editingTabacoRevision}
          >
            Volver
          </Button>
          <Button
            variant="contained"
            color={diffRevision === 0 && diferenciaTabacoTotal === 0 ? 'primary' : 'warning'}
            onClick={() => cerrarCaja(pendingCloseTabacoDetalle, pendingCloseTabacoTotals)}
            disabled={isSubmitting || editingArqueoRevision || editingTabacoRevision}
          >
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Confirmar cierre'}
          </Button>
        </DialogActions>
      </Dialog>

      {pdfUrl && (
        <Dialog
          open
          onClose={() => {
            clearPdf();
            handleLogout();
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography fontWeight={900}>Comprobante de Cierre</Typography>

            <IconButton
              onClick={() => {
                clearPdf();
                handleLogout();
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ p: 0, height: 600 }}>
            <iframe
              src={pdfUrl}
              title="Cierre de Caja"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                clearPdf();
                handleLogout();
              }}
            >
              Cerrar
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                const w = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                try {
                  w?.print?.();
                } catch (_) {}
                handleLogout();
              }}
            >
              Imprimir
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {nextAction === 'abrir' && (
        <ConteoTabacoModal
          key={`${nextAction || 'none'}-${usarCierreAnteriorTabaco ? 'cierre-anterior' : 'blanco'}-${ultimoCierreTabaco?.caja_id || 'sin-caja'}`}
          open={tabacoModalOpen}
          mode="abrir"
          cajaId={caja?.id}
          detalleTabacoEntrega={tabacoBaseApertura}
          ventasTababoTotal={ventasTabacoTotal}
          ventasTabacoTotal={ventasTabacoTotal}
          tabacoBaseOrigen={usarCierreAnteriorTabaco ? 'ultimo_cierre' : 'blanco'}
          ultimoCierreTabaco={ultimoCierreTabaco}
          onConfirm={handleTabacoConfirm}
          onCancel={handleCancelarConteoTabaco}
        />
      )}

      <ConteoTabacoCierreModal
        open={tabacoModalOpen && nextAction === 'cerrar'}
        onClose={handleCancelarConteoTabaco}
        cajaData={caja}
        onConfirm={handleTabacoConfirm}
        loading={isSubmitting}
      />
    </>
  );
}

CajaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};